import { runScan, searchGrounding, getLocationSuggestions, geocodeLocation } from '@/src/services/apiServices';
import React, { createContext, useContext, useReducer, useCallback, useRef, useEffect, ReactNode } from 'react';
import { FeatureType, AppState, BusinessIdea, LocationSuggestion, RecentScan, AnalysisResult, LandingLoginSource } from '@/src/types';
import { useAuth } from '@/src/contexts/AuthContext';
import { sanitizeData, generateVentureId } from '@/src/utils/formatters';
import { isLikelyLocation } from '@/src/utils/validation';
import { db, collection, addDoc, getDocs } from '@/firebase';

interface SearchState extends AppState {
  activeSuggestionHub: 'launchpad' | 'discovery' | 'locality' | null;
  isMapPickerOpen: boolean;
  mapPickerTarget: 'launchpad' | 'discovery';
  isCurrencyMenuOpen: boolean;
  isCountryMenuOpen: boolean;
  countrySearch: string;
  currencySearch: string;
  launchpadSectorWarning: boolean;
  densitySectorWarning: boolean;
  locSuggestions: LocationSuggestion[];
  isFetchingSuggestions: boolean;
  searchCounts: { launchpad: number, discovery: number, view: number };
  isScrutinyActive: boolean;
  landingLoginSource: LandingLoginSource | null;
}

type SearchAction =
  | { type: 'SET_STATE'; payload: Partial<SearchState> }
  | { type: 'SET_LANDING_LOGIN_SOURCE'; payload: LandingLoginSource | null }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_RESULT'; payload: { result: AnalysisResult | null, activeFeature: FeatureType | null } }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_REVEALED'; payload: boolean }
  | { type: 'SET_SUGGESTIONS'; payload: LocationSuggestion[] }
  | { type: 'SET_FETCHING_SUGGESTIONS'; payload: boolean }
  | { type: 'SET_ACTIVE_SUGGESTION_HUB'; payload: 'launchpad' | 'discovery' | 'locality' | null }
  | { type: 'SET_MAP_PICKER'; payload: { isOpen: boolean, target?: 'launchpad' | 'discovery' } }
  | { type: 'SET_MENU_OPEN'; payload: { menu: 'currency' | 'country', isOpen: boolean } }
  | { type: 'SET_SEARCH_TERM'; payload: { type: 'country' | 'currency', term: string } }
  | { type: 'SET_WARNING'; payload: { type: 'launchpad' | 'density', hasWarning: boolean } }
  | { type: 'SET_ACTIVE_FEATURE'; payload: FeatureType | null }
  | { type: 'INCREMENT_SEARCH_COUNT'; payload: 'launchpad' | 'discovery' | 'view' }
  | { type: 'RESET_SEARCH' };

const initialState: SearchState = {
  launchpadLocation: '', 
  launchpadSector: '', 
  discoveryLocation: '', 
  discoveryLocality: '',
  densitySector: '',
  discoveryCountry: '🌎 Worldwide', 
  recentScans: [],
  preferredCurrency: 'USD', 
  preferredLanguage: 'English (US)',
  budgetLimit: 5000,
  isBudgetActive: false, 
  skills: '', 
  coords: null, 
  activeFeature: null,
  loading: false, 
  result: null, 
  error: null,
  isRevealed: false,
  activeSuggestionHub: null,
  isMapPickerOpen: false,
  mapPickerTarget: 'launchpad',
  isCurrencyMenuOpen: false,
  isCountryMenuOpen: false,
  countrySearch: '',
  currencySearch: '',
  launchpadSectorWarning: false,
  densitySectorWarning: false,
  locSuggestions: [],
  isFetchingSuggestions: false,
  optimizeBusinesses: [],
  searchCounts: { launchpad: 0, discovery: 0, view: 0 },
  isScrutinyActive: false,
  landingLoginSource: localStorage.getItem('landingLoginSource') as any || null
};

function searchReducer(state: SearchState, action: SearchAction): SearchState {
  switch (action.type) {
    case 'SET_LANDING_LOGIN_SOURCE':
      if (action.payload) {
        localStorage.setItem('landingLoginSource', action.payload);
      } else {
        localStorage.removeItem('landingLoginSource');
      }
      return { ...state, landingLoginSource: action.payload };
    case 'SET_STATE':
      return { ...state, ...action.payload };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_RESULT':
      return { ...state, result: action.payload.result, activeFeature: action.payload.activeFeature, loading: false, isRevealed: true };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'SET_REVEALED':
      return { ...state, isRevealed: action.payload };
    case 'SET_SUGGESTIONS':
      return { ...state, locSuggestions: action.payload };
    case 'SET_FETCHING_SUGGESTIONS':
      return { ...state, isFetchingSuggestions: action.payload };
    case 'SET_ACTIVE_SUGGESTION_HUB':
      return { ...state, activeSuggestionHub: action.payload };
    case 'SET_MAP_PICKER':
      return { 
        ...state, 
        isMapPickerOpen: action.payload.isOpen, 
        mapPickerTarget: action.payload.target || state.mapPickerTarget 
      };
    case 'SET_MENU_OPEN':
      if (action.payload.menu === 'currency') {
        return { ...state, isCurrencyMenuOpen: action.payload.isOpen };
      } else {
        return { ...state, isCountryMenuOpen: action.payload.isOpen };
      }
    case 'SET_SEARCH_TERM':
      if (action.payload.type === 'country') {
        return { ...state, countrySearch: action.payload.term };
      } else {
        return { ...state, currencySearch: action.payload.term };
      }
    case 'SET_WARNING':
      if (action.payload.type === 'launchpad') {
        return { ...state, launchpadSectorWarning: action.payload.hasWarning };
      } else {
        return { ...state, densitySectorWarning: action.payload.hasWarning };
      }
    case 'SET_ACTIVE_FEATURE':
      return { ...state, activeFeature: action.payload };
    case 'INCREMENT_SEARCH_COUNT':
      return { 
        ...state, 
        searchCounts: { 
          ...state.searchCounts, 
          [action.payload]: state.searchCounts[action.payload] + 1 
        } 
      };
    case 'RESET_SEARCH':
      return { ...state, isRevealed: false, result: null, activeFeature: null };
    default:
      return state;
  }
}

interface SearchContextType {
  state: SearchState;
  dispatch: React.Dispatch<SearchAction>;
  handleRunScan: (type: FeatureType, manualLoc?: string, manualSector?: string) => Promise<void>;
  handleLocInputChange: (val: string, hub: 'launchpad' | 'discovery' | 'locality') => void;
  handleLaunchpadSectorChange: (val: string) => void;
  handleDensityInputChange: (val: string) => void;
  handleSuggestionSelect: (name: string, hub: 'launchpad' | 'discovery' | 'locality') => Promise<void>;
  handleMapSelection: (name: string, coords: { lat: number; lng: number }) => void;
  handleRecentClick: (scan: RecentScan) => void;
}

const SearchContext = createContext<SearchContextType | undefined>(undefined);

export const SearchProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(searchReducer, initialState);
  const { user, isLoggedIn, setIsSignUpModalOpen, userProfile } = useAuth();

  const suggestionTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastRequestId = useRef(0);

  const fetchSuggestions = useCallback((value: string, hub: 'launchpad' | 'discovery' | 'locality') => {
    if (suggestionTimeout.current) clearTimeout(suggestionTimeout.current);
    if (value.length < 3) {
      dispatch({ type: 'SET_SUGGESTIONS', payload: [] });
      return;
    }

    const requestId = ++lastRequestId.current;

    suggestionTimeout.current = setTimeout(async () => {
      dispatch({ type: 'SET_FETCHING_SUGGESTIONS', payload: true });
      try {
        const sector = hub === 'launchpad' ? state.launchpadSector : state.densitySector;
        const country = hub === 'launchpad' ? undefined : state.discoveryCountry;
        const suggestions = await getLocationSuggestions(value, sector, country);

        if (requestId === lastRequestId.current) {
          dispatch({ type: 'SET_SUGGESTIONS', payload: sanitizeData(suggestions) as any });
        }
      } catch (err) {
        if (requestId === lastRequestId.current) {
          console.error("Suggestion fetch failed", err);
        }
      } finally {
        if (requestId === lastRequestId.current) {
          dispatch({ type: 'SET_FETCHING_SUGGESTIONS', payload: false });
        }
      }
    }, 800);

  }, [state.launchpadSector, state.densitySector, state.discoveryCountry]);

  const handleLocInputChange = useCallback((val: string, hub: 'launchpad' | 'discovery' | 'locality') => {
    if (hub === 'launchpad') {
      dispatch({ type: 'SET_STATE', payload: { launchpadLocation: val } });
    } else if (hub === 'discovery') {
      dispatch({ type: 'SET_STATE', payload: { discoveryLocation: val } });
    } else {
      dispatch({ type: 'SET_STATE', payload: { discoveryLocality: val } });
    }
    dispatch({ type: 'SET_ACTIVE_SUGGESTION_HUB', payload: hub });
    fetchSuggestions(val, hub);
  }, [fetchSuggestions]);

  const handleLaunchpadSectorChange = useCallback((val: string) => {
    dispatch({ type: 'SET_STATE', payload: { launchpadSector: val } });
    dispatch({ 
      type: 'SET_WARNING', 
      payload: { 
        type: 'launchpad', 
        hasWarning: val.length > 0 && isLikelyLocation(val, state.launchpadLocation, state.discoveryLocation) 
      } 
    });
  }, [state.launchpadLocation, state.discoveryLocation]);

  const handleDensityInputChange = useCallback((val: string) => {
    dispatch({ type: 'SET_STATE', payload: { densitySector: val } });
    dispatch({ 
      type: 'SET_WARNING', 
      payload: { 
        type: 'density', 
        hasWarning: val.length > 0 && isLikelyLocation(val, state.launchpadLocation, state.discoveryLocation) 
      } 
    });
  }, [state.launchpadLocation, state.discoveryLocation]);

  const handleSuggestionSelect = useCallback(async (name: string, hub: 'launchpad' | 'discovery' | 'locality') => {
    if (hub === 'launchpad') {
      dispatch({ type: 'SET_STATE', payload: { launchpadLocation: name } });
    } else if (hub === 'discovery') {
      dispatch({ type: 'SET_STATE', payload: { discoveryLocation: name } });
    } else {
      dispatch({ type: 'SET_STATE', payload: { discoveryLocality: name } });
    }
    dispatch({ type: 'SET_SUGGESTIONS', payload: [] });
    dispatch({ type: 'SET_ACTIVE_SUGGESTION_HUB', payload: null });

    const geo = await geocodeLocation(name);
    if (geo) {
      dispatch({ type: 'SET_STATE', payload: { coords: { lat: geo.lat, lng: geo.lng } } });
    }
  }, []);

  const handleRunScan = useCallback(async (type: FeatureType, manualLoc?: string, manualSector?: string) => {
    const isLaunchpad = type === FeatureType.Scan || type === FeatureType.GapFinder;

    let loc = manualLoc || (isLaunchpad ? state.launchpadLocation : state.discoveryLocation);
    const sector = manualSector || (isLaunchpad ? state.launchpadSector : state.densitySector);
    let country: string | undefined = undefined;

    // DISCOVERY HUB: build geographic context from country + locality
    if (type === FeatureType.DensityScout || type === FeatureType.ReverseDiscovery) {
      const isWorldwide = state.discoveryCountry.includes('Worldwide');
      const cleanCountry = isWorldwide ? '' : state.discoveryCountry.replace(/[\uD800-\uDBFF][\uDC00-\uDFFF]/g, '').trim();
      const locality = state.discoveryLocality.trim();
      const suiteLoc = locality && cleanCountry ? `${locality}, ${cleanCountry}` : (locality || cleanCountry || '');

      if (type === FeatureType.DensityScout) {
        loc = suiteLoc || 'the world';
      } else {
        if (loc && suiteLoc) {
          loc = `${loc}, ${suiteLoc}`;
        } else {
          loc = loc || suiteLoc || 'the world';
        }
      }

      country = cleanCountry || undefined;
    }

    const combinedLoc = loc;

    if (!combinedLoc && !sector) return;

    if (!isLoggedIn) {
      if (type === FeatureType.Scan && state.searchCounts.launchpad >= 1) {
        dispatch({ type: 'SET_LANDING_LOGIN_SOURCE', payload: 'launchpad' });
        setIsSignUpModalOpen(true);
        return;
      }
      if ((type === FeatureType.DensityScout || type === FeatureType.ReverseDiscovery) && state.searchCounts.discovery >= 1) {
        dispatch({ type: 'SET_LANDING_LOGIN_SOURCE', payload: 'discovery' });
        setIsSignUpModalOpen(true);
        return;
      }
    } else {
      const tier = userProfile?.subscriptionTier || 'free';
      if (tier === 'free') {
        try {
          const scanRef = collection(db, 'users', user!.uid, 'scans');
          const scansSnap = await getDocs(scanRef);
          const pastScans = scansSnap.docs.map(d => d.data());

          const launchpadScansCount = pastScans.filter((s: any) => s.type === FeatureType.Scan).length;
          const discoveryScansCount = pastScans.filter((s: any) => s.type === FeatureType.DensityScout || s.type === FeatureType.ReverseDiscovery).length;

          if (type === FeatureType.Scan && launchpadScansCount >= 2) {
            window.dispatchEvent(new CustomEvent('trigger-upgrade'));
            return;
          }

          if ((type === FeatureType.DensityScout || type === FeatureType.ReverseDiscovery) && discoveryScansCount >= 1) {
            window.dispatchEvent(new CustomEvent('trigger-upgrade'));
            return;
          }
        } catch (e) {
          console.error("Failed to query subscription scan limits:", e);
        }
      }
    }

    dispatch({ type: 'SET_STATE', payload: { loading: true, error: null, result: null, activeFeature: type } });
    dispatch({ type: 'SET_SUGGESTIONS', payload: [] });

    const newScan: RecentScan = { 
      id: `scan-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`, 
      location: combinedLoc || 'Global Search', 
      sector, 
      type, 
      timestamp: Date.now() 
    };
    const updatedScans = [newScan, ...state.recentScans.filter(s => s.location !== (combinedLoc || 'Global Search'))].slice(0, 5);
    dispatch({ type: 'SET_STATE', payload: { recentScans: sanitizeData(updatedScans) as RecentScan[] } });

    try {
      // 1. Ground with Brave Search first
      const searchQuery = `${sector || 'business'} opportunities in ${combinedLoc}${country ? ` ${country}` : ''}`.trim();
      let synthesis = '';
      try {
        const ground = await searchGrounding(searchQuery, true);
        synthesis = ground.synthesis || '';
        console.log('Grounded with', ground.results?.length, 'sources');
      } catch (e) {
        console.warn('Search grounding failed, continuing without it:', e);
      }

      // 2. Run scan with real-time intel
      // Launchpad: country is undefined (no country picker)
      // Discovery: country is the cleaned discoveryCountry
      const res: any = await runScan(
        type,
        combinedLoc,
        state.coords,
        sector,
        country,
        synthesis
      );

      if (res.ideas) {
        const processedIdeas = (res.ideas as any[]).map((idea: any, idx: number) => {
          const rawTitle = idea.title || idea.name || `Venture ${idx + 1}`;
          const loc = idea.locationName || combinedLoc || '';
          const uniqueId = generateVentureId(rawTitle, loc, idea.id);
          return {
            ...idea,
            id: uniqueId,
            locationName: idea.locationName || combinedLoc || '',
          };
        });
        res.ideas = sanitizeData(processedIdeas) as BusinessIdea[];
      }

      if (user) {
        try {
          const avgScore = res.ideas?.length > 0 
            ? Math.round(res.ideas.reduce((acc: number, idea: any) => acc + (idea.marketDemandScore || 0), 0) / res.ideas.length)
            : 0;

          const scanRef = collection(db, 'users', user.uid, 'scans');
          await addDoc(scanRef, {
            location: combinedLoc || 'Global',
            sector: sector || 'General Market',
            budget: state.budgetLimit,
            score: avgScore,
            date: new Date().toISOString(),
            results: JSON.stringify(res)
          });
        } catch (e) {
          console.error('Failed to save scan:', e);
        }
      }

      if (!isLoggedIn) {
        dispatch({ type: 'INCREMENT_SEARCH_COUNT', payload: type === FeatureType.Scan ? 'launchpad' : 'discovery' });
      }

      dispatch({ type: 'SET_RESULT', payload: { result: res, activeFeature: type } });
    } catch (err: any) {
      console.error('[handleRunScan] Analysis failed:', err);
      dispatch({ type: 'SET_ERROR', payload: err?.message || err?.toString() || 'Intelligence Failure' });
    }
  }, [state, isLoggedIn, user, setIsSignUpModalOpen, userProfile]);

  const handleMapSelection = useCallback((name: string, coords: { lat: number; lng: number }) => {
    if (state.mapPickerTarget === 'launchpad') {
      dispatch({ type: 'SET_STATE', payload: { launchpadLocation: name, coords } });
    } else {
      dispatch({ type: 'SET_STATE', payload: { discoveryLocation: name, coords } });
    }
  }, [state.mapPickerTarget]);

  const handleRecentClick = useCallback((scan: RecentScan) => {
    if (scan.type === FeatureType.Scan) {
      dispatch({ type: 'SET_STATE', payload: { launchpadLocation: scan.location, launchpadSector: scan.sector || '' } });
    } else {
      dispatch({ type: 'SET_STATE', payload: { discoveryLocation: scan.location, densitySector: scan.sector || '' } });
    }
    handleRunScan(scan.type, scan.location, scan.sector);
  }, [handleRunScan]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hub = params.get('hub');
    const location = params.get('location');
    const sector = params.get('sector');
    const country = params.get('country');
    const locality = params.get('locality');

    if (hub === 'discovery') {
      dispatch({ 
        type: 'SET_STATE', 
        payload: { 
          discoveryLocation: location || '', 
          densitySector: sector || '',
          discoveryCountry: country || '🌎 Worldwide',
          discoveryLocality: locality || ''
        } 
      });
      if (location || sector) {
        handleRunScan(FeatureType.ReverseDiscovery, location || undefined, sector || undefined);
      }
    } else if (hub === 'launchpad') {
      dispatch({ 
        type: 'SET_STATE', 
        payload: { 
          launchpadLocation: location || '', 
          launchpadSector: sector || '' 
        } 
      });
      if (location || sector) {
        handleRunScan(FeatureType.Scan, location || undefined, sector || undefined);
      }
    }
  }, []);

  useEffect(() => {
    if (state.result && state.activeFeature) {
      const params = new URLSearchParams(window.location.search);
      if (state.activeFeature === FeatureType.Scan) {
        params.set('hub', 'launchpad');
        params.set('location', state.launchpadLocation);
        params.set('sector', state.launchpadSector);
      } else {
        params.set('hub', 'discovery');
        params.set('location', state.discoveryLocation);
        params.set('sector', state.densitySector);
        params.set('country', state.discoveryCountry);
        params.set('locality', state.discoveryLocality);
      }
      const newUrl = `${window.location.pathname}?${params.toString()}`;
      window.history.replaceState({}, '', newUrl);
    }
  }, [state.result, state.activeFeature, state.launchpadLocation, state.launchpadSector, state.discoveryLocation, state.densitySector, state.discoveryCountry, state.discoveryLocality]);

  const value = {
    state,
    dispatch,
    handleRunScan,
    handleLocInputChange,
    handleLaunchpadSectorChange,
    handleDensityInputChange,
    handleSuggestionSelect,
    handleMapSelection,
    handleRecentClick
  };

  return (
    <SearchContext.Provider value={value}>
      {children}
    </SearchContext.Provider>
  );
};

export const useSearch = () => {
  const context = useContext(SearchContext);
  if (context === undefined) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
};
